import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { BookOpen, Users, MessageCircle, Check } from 'lucide-react';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { Card } from './ui/Card';
export function TrainingSection() {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    // Simulate API call
    setTimeout(() => {
      setIsLoading(false);
      setIsSubmitted(true);
    }, 1500);
  };
  return <section id="training" className="py-24 bg-white" data-id="element-133">
      <div className="container mx-auto px-4" data-id="element-134">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-135">
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-6" data-id="element-136">
            Ready to Master This Mechanism?
          </h2>
          <p className="text-xl text-slate-600" data-id="element-137">
            Join thousands of ophthalmologists who have already integrated
            Vabysmo into their practice.
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8 mb-16" data-id="element-138">
          {[{
          icon: <BookOpen className="w-8 h-8 text-teal-500" data-id="element-139" />,
          title: 'Mechanism Mastery',
          desc: 'Deep dive into the science of dual pathway inhibition and Ang-2 targeting.'
        }, {
          icon: <Users className="w-8 h-8 text-blue-500" data-id="element-140" />,
          title: 'Patient Selection',
          desc: 'Learn how to identify the patients who will benefit most from switching.'
        }, {
          icon: <MessageCircle className="w-8 h-8 text-purple-500" data-id="element-141" />,
          title: 'Early Adopter Stories',
          desc: 'Hear real-world case studies from peers who have seen the difference.'
        }].map((feature, idx) => <motion.div key={idx} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          delay: idx * 0.1
        }} data-id="element-142">
              <Card className="p-8 h-full hover:shadow-lg transition-shadow duration-300 border-slate-100" data-id="element-143">
                <div className="bg-slate-50 w-16 h-16 rounded-2xl flex items-center justify-center mb-6" data-id="element-144">
                  {feature.icon}
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3" data-id="element-145">
                  {feature.title}
                </h3>
                <p className="text-slate-600 leading-relaxed" data-id="element-146">{feature.desc}</p>
              </Card>
            </motion.div>)}
        </div>

        <div className="max-w-2xl mx-auto" data-id="element-147">
          <Card variant="elevated" className="p-8 md:p-10 border-t-4 border-teal-500" data-id="element-148">
            {isSubmitted ? <motion.div initial={{
            opacity: 0,
            scale: 0.9
          }} animate={{
            opacity: 1,
            scale: 1
          }} className="text-center py-12" data-id="element-149">
                <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-150">
                  <Check className="w-10 h-10 text-green-600" data-id="element-151" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-2" data-id="element-152">
                  Request Received
                </h3>
                <p className="text-slate-600" data-id="element-153">
                  Thank you for your interest. A Vabysmo representative will
                  contact you shortly to schedule your training session.
                </p>
                <Button variant="outline" className="mt-8" onClick={() => setIsSubmitted(false)} data-id="element-154">
                  Schedule Another
                </Button>
              </motion.div> : <form onSubmit={handleSubmit} className="space-y-6" data-id="element-155">
                <div className="text-center mb-8" data-id="element-156">
                  <h3 className="text-2xl font-bold text-slate-900" data-id="element-157">
                    Schedule Your Training
                  </h3>
                  <p className="text-slate-500 mt-2" data-id="element-158">
                    Fill out the form below to get started.
                  </p>
                </div>

                <div className="grid md:grid-cols-2 gap-6" data-id="element-159">
                  <Input label="Full Name" placeholder="Dr. Jane Smith" required data-id="element-160" />
                  <Input label="Email Address" type="email" placeholder="jane.smith@clinic.com" required data-id="element-161" />
                </div>

                <div className="grid md:grid-cols-2 gap-6" data-id="element-162">
                  <Input label="Phone Number" type="tel" placeholder="(555) 123-4567" required data-id="element-163" />
                  <div className="space-y-1.5" data-id="element-164">
                    <label className="block text-sm font-medium text-slate-700" data-id="element-165">
                      Timezone
                    </label>
                    <select className="flex w-full rounded-lg border border-slate-200 bg-white px-3 py-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500" data-id="element-166">
                      <option data-id="element-167">Pacific Time (PT)</option>
                      <option data-id="element-168">Mountain Time (MT)</option>
                      <option data-id="element-169">Central Time (CT)</option>
                      <option data-id="element-170">Eastern Time (ET)</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-1.5" data-id="element-171">
                  <label className="block text-sm font-medium text-slate-700" data-id="element-172">
                    Preferred Time
                  </label>
                  <select className="flex w-full rounded-lg border border-slate-200 bg-white px-3 py-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-teal-500" data-id="element-173">
                    <option data-id="element-174">Morning (8am - 12pm)</option>
                    <option data-id="element-175">Afternoon (12pm - 4pm)</option>
                    <option data-id="element-176">Evening (4pm - 8pm)</option>
                  </select>
                </div>

                <Button type="submit" className="w-full" size="lg" isLoading={isLoading} data-id="element-177">
                  SCHEDULE TRAINING
                </Button>

                <p className="text-xs text-center text-slate-400 mt-4" data-id="element-178">
                  By submitting this form, you agree to receive communications
                  from Vabysmo education team.
                </p>
              </form>}
          </Card>
        </div>
      </div>
    </section>;
}